import { FormEvent, useCallback, useEffect, useState } from "react";
import {
  adminApi,
  SentNotificationRow,
  AdminApiError,
  PushDispatchResult,
} from "../lib/adminApi";
import { LoadingState, ErrorState, ConfirmDialog } from "../components/Common";

const CATEGORIES = ["general", "reminder", "insight", "admin", "system"];
const PAGE_SIZE = 20;
// Must match PUSH_ROUTES in the Worker (src/push/pushDispatcher.ts): the
// in-app screen a push opens. "" = just open the app.
const ROUTES: Array<{ value: string; label: string }> = [
  { value: "", label: "Open the Notifications tab (default)" },
  { value: "home", label: "Home" },
  { value: "health", label: "Health" },
  { value: "ai_talk", label: "AI Talk" },
  { value: "profile", label: "Profile" },
];

/** Honest, plain-language summary of the push attempt. Never says
 * "delivered" -- FCM acceptance means the message was handed to Google, not
 * that a phone displayed it. */
function describePush(push: PushDispatchResult): string {
  switch (push.status) {
    case "sent":
      return `Push accepted by FCM for ${push.sent} device${push.sent === 1 ? "" : "s"}`;
    case "partial":
      return `Push accepted for ${push.sent} device(s), failed for ${push.failed}${
        push.invalidTokensDeactivated ? ` (${push.invalidTokensDeactivated} stale token(s) deactivated)` : ""
      }`;
    case "failed":
      return `Push failed for ${push.failed} device(s)${push.errorCode ? ` (${push.errorCode})` : ""}`;
    case "no_devices":
      return "No registered devices to push to (in-app only)";
    case "not_configured":
      return "Push is not configured on the server yet (in-app only)";
    default:
      return `Push could not be attempted${push.errorCode ? ` (${push.errorCode})` : ""}`;
  }
}

export function Notifications() {
  const [targetType, setTargetType] = useState<"user" | "broadcast">("user");
  const [targetEmail, setTargetEmail] = useState("");
  const [category, setCategory] = useState("admin");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [route, setRoute] = useState("");
  const [pendingDelete, setPendingDelete] = useState<{
    row: SentNotificationRow;
    scope: "single" | "group";
  } | null>(null);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<SentNotificationRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const loadSent = useCallback(() => {
    setLoading(true);
    setListError(null);
    adminApi
      .listSentNotifications({ page, pageSize: PAGE_SIZE })
      .then((r) => {
        setRows(r.rows);
        setTotal(r.total);
      })
      .catch((e: AdminApiError) => setListError(e.message))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    loadSent();
  }, [loadSent]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    const { row, scope } = pendingDelete;
    setPendingDelete(null);
    setError(null);
    setFeedback(null);
    try {
      const result = await adminApi.deleteNotification(row.id, scope);
      setFeedback(
        `Deleted ${result.deletedCount} notification${result.deletedCount === 1 ? "" : "s"} from the system.`
      );
      loadSent();
    } catch (e) {
      setError((e as AdminApiError).message);
    }
  }

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFeedback(null);
    if (targetType === "user" && !targetEmail.trim()) {
      setError("Enter the recipient's email.");
      return;
    }
    if (!title.trim() || !body.trim()) {
      setError("Title and message are both required.");
      return;
    }
    setSending(true);
    try {
      const result = await adminApi.sendNotification({
        targetType,
        targetEmail: targetType === "user" ? targetEmail.trim() : undefined,
        category,
        title: title.trim(),
        body: body.trim(),
        deepLink: route || undefined,
      });
      setFeedback(
        `In-app notification created for ${result.recipientCount} recipient${
          result.recipientCount === 1 ? "" : "s"
        }. ${describePush(result.push)}${
          result.push.skippedOverLimit > 0
            ? ` · ${result.push.skippedOverLimit} more device(s) over the per-send limit were not pushed`
            : ""
        }.`
      );
      setTitle("");
      setBody("");
      setPage(1);
      loadSent();
    } catch (e) {
      setError((e as AdminApiError).message);
    } finally {
      setSending(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Notifications</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Creates a real row in each recipient's in-app Notifications tab (Supabase-backed,
        RLS-protected — only this admin route can create one), then tries to push it to their
        registered devices through Firebase Cloud Messaging. The result below says exactly what
        happened; "accepted by FCM" means Google took the message, not that a phone showed it.
        Pushes are limited per send (free Worker plan), so large broadcasts may reach only some devices.
      </p>

      <form
        onSubmit={handleSend}
        className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={targetType === "user"}
              onChange={() => setTargetType("user")}
            />
            Single user
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={targetType === "broadcast"}
              onChange={() => setTargetType("broadcast")}
            />
            Broadcast to everyone
          </label>
        </div>

        {targetType === "user" && (
          <input
            placeholder="Recipient email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          />
        )}

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <select
          value={route}
          onChange={(e) => setRoute(e.target.value)}
          aria-label="Screen to open when tapped"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        >
          {ROUTES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        <input
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        />
        <textarea
          placeholder="Message"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
        />

        {error && <ErrorState message={error} />}
        {feedback && (
          <div className="rounded-lg bg-brand-light px-3 py-2 text-sm text-brand dark:bg-brand/20 dark:text-white">
            {feedback}
          </div>
        )}

        <button
          type="submit"
          disabled={sending}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60"
        >
          {sending ? "Sending..." : "Send"}
        </button>
      </form>

      <div>
        <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Recently sent</h2>
        {listError && <ErrorState message={listError} />}
        {loading ? (
          <LoadingState />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase text-gray-500 dark:border-gray-800 dark:text-gray-400">
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Message</th>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      Nothing sent yet.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.category}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r.title}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.body}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{r.userId}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <button
                        onClick={() => setPendingDelete({ row: r, scope: "group" })}
                        className="mr-2 rounded-lg border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setPendingDelete({ row: r, scope: "single" })}
                        className="text-xs text-gray-500 underline hover:text-gray-700 dark:text-gray-400"
                      >
                        only this recipient
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>
            {total} sent · page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40 dark:border-gray-700"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40 dark:border-gray-700"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        danger
        title={
          pendingDelete?.scope === "group"
            ? "Delete this message for everyone?"
            : "Delete for this recipient only?"
        }
        description={
          pendingDelete?.scope === "group"
            ? `"${pendingDelete?.row.title}" will be permanently removed from the system, including every recipient's in-app inbox from the same send. This cannot be undone. A push notification already shown on a phone cannot be recalled.`
            : `"${pendingDelete?.row.title}" will be permanently removed from this one recipient's in-app inbox. This cannot be undone. A push notification already shown on their phone cannot be recalled.`
        }
        confirmLabel="Delete permanently"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
